import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { TKN, TOLERANCIA_EXPIRACAO_TOKEN_MS, URL, USR } from '@/modules/shared/lib/consts'
import { tokenExpirado } from '@/modules/shared/lib/jwt'
import { CapacitorHttp } from "@capacitor/core"
import useRequest, {
  isAnyRequestPendingGlobal,
  didIdentityChangeRecentlyGlobal,
  markIdentityChangedGlobal
} from '@/modules/shared/hooks/use-request'
import { safeJsonParse, safeLocalRemoveAuth } from '@/modules/shared/lib/utils'
import { revalidarAuth } from '@/modules/shared/hooks/use-auth-revalidate'
import { appendAppVersionHeaders } from '@/modules/shared/lib/app-version'
import { toast } from "sonner"

type HttpLike = (path: string, body?: Record<string, unknown>, options?: { identityOverride?: Identity }) => Promise<{ success: boolean; data?: any; err?: unknown }>;

const _normalizarBool = (v: unknown): boolean => {
  if (v === true || v === "true" || v === 1 || v === "1" || v === "S" || v === "s") return true;
  return false;
};

const complementarDadosProfessorSeNecessario = async (
  identity: Identity,
  post: HttpLike
): Promise<Identity> => {
  if (identity?.role !== "PROFESSOR") return identity;
  if (typeof identity.utiliza_redacao === "boolean") return identity;

  try {
    const res = await post("/proc/obter_professores", { id: identity.id }, { identityOverride: identity });
    const row = Array.isArray(res?.data?.dados) ? (res.data.dados as unknown[])[0] : null;
    const obj = (row ?? (res?.data as any) ?? null) as Record<string, unknown> | null;
    if (!obj || typeof obj !== "object") return identity;
    const utiliza = "utiliza_redacao" in obj ? _normalizarBool(obj.utiliza_redacao) : identity.utiliza_redacao;
    return {
      ...identity,
      utiliza_redacao: typeof utiliza === "boolean" ? utiliza : false,
    };
  } catch {
    return identity;
  }
};

const SUPER_USR_ORIGINAL = "@5-techs/super_original_user"
const SUPER_TKN_ORIGINAL = "@5-techs/super_original_token"

type AuthProviderState = {
  identity: Identity | null
  isImpersonating: boolean
  setIdentity: (identity: Identity | null) => void
  setSuperSchoolId: (schoolId: string | undefined) => void
  login?: (user: string, pwd: string) => Promise<void>
  logout?: () => void
  startImpersonate?: (email: string) => Promise<Identity>
  stopImpersonate?: () => void
}

const AuthProviderContext = createContext<AuthProviderState>({
  identity: null,
  isImpersonating: false,
  setIdentity: () => { },
  setSuperSchoolId: () => { }
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { post } = useRequest()

  const revalidacaoRodandoRef = useRef<boolean>(false)
  const sessaoIniciadaViaLoginRef = useRef<boolean>(false)
  const pathnameRef = useRef<string>(typeof window !== 'undefined' ? window.location.pathname : '/')
  const [pathnameState, setPathnameState] = useState<string>(pathnameRef.current)
  const [, forceTick] = useState<number>(0)

  useEffect(() => {
    const onPopState = () => {
      pathnameRef.current = window.location.pathname
      setPathnameState(window.location.pathname)
    }
    const onHashChange = () => {
      pathnameRef.current = window.location.pathname
      setPathnameState(window.location.pathname)
    }
    window.addEventListener('popstate', onPopState)
    window.addEventListener('hashchange', onHashChange)
    const _pushState = history.pushState.bind(history)
    const _replaceState = history.replaceState.bind(history)
    history.pushState = function (...args) {
      const r = _pushState(...args)
      onPopState()
      return r
    }
    history.replaceState = function (...args) {
      const r = _replaceState(...args)
      onPopState()
      return r
    }
    return () => {
      window.removeEventListener('popstate', onPopState)
      window.removeEventListener('hashchange', onHashChange)
      history.pushState = _pushState
      history.replaceState = _replaceState
    }
  }, [])

  const acionarInicioIntervaloAposLogin = useCallback(() => {
    sessaoIniciadaViaLoginRef.current = true
    forceTick(x => x + 1)
  }, [])
  const desligarIntervaloAposLogout = useCallback(() => {
    sessaoIniciadaViaLoginRef.current = false
    forceTick(x => x + 1)
  }, [])

  const [identity, _setIdentity] = useState<Identity | null>(() => {
    const json = localStorage.getItem(USR)
    const parsed = safeJsonParse<Identity>(json)
    if (parsed && typeof parsed.token === "string" && parsed.token.trim() !== "") {
      sessaoIniciadaViaLoginRef.current = true
    }
    return parsed
  })

  const isImpersonating = useMemo(() => !!identity?.impersonated_by, [identity])

  const setIdentity = useCallback((obj: Identity | null) => {
    // 🛡️ Race: marca que identity acabou de mudar → revalidar espera 3s
    markIdentityChangedGlobal()

    if (obj && typeof obj === "object" && typeof obj.token === "string") {
      try { localStorage.setItem(TKN, obj.token) } catch {}
    }
    if (obj === null) {
      try { localStorage.setItem(USR, JSON.stringify(null)) } catch {}
      _setIdentity(null)
      return
    }
    _setIdentity((prev) => {
      const merged: Identity = {
        ...(prev ?? {} as any),
        ...(obj ?? {} as any),
      } as Identity
      try { localStorage.setItem(USR, JSON.stringify(merged)) } catch {}
      return merged
    })
  }, [])

  const setSuperSchoolId = useCallback((schoolId: string | undefined) => {
    markIdentityChangedGlobal()
    _setIdentity((prev) => {
      if (!prev) return prev
      if (prev.school_id === schoolId) return prev
      const updated = { ...prev, school_id: schoolId }
      try { localStorage.setItem(USR, JSON.stringify(updated)) } catch {}
      return updated
    })
  }, [])

  const pn = pathnameState ?? pathnameRef.current
  const telaLogin = pn === '/login' || pn.startsWith('/login')
  const intervaloDeveRodar = !telaLogin && !!identity?.token && sessaoIniciadaViaLoginRef.current

  useEffect(() => {
    if (!intervaloDeveRodar) return;
    let intervalId: number | null = null;
    let cancelado = false;

    const rodar = async () => {
      if (identity?.token && tokenExpirado(identity.token, TOLERANCIA_EXPIRACAO_TOKEN_MS)) {
        toast.error('Sessão expirada. Faça login novamente.');
        safeLocalRemoveAuth();
        sessaoIniciadaViaLoginRef.current = false;
        _setIdentity(null);
        window.location.hash = '#';
        window.location.replace('/');
        return;
      }

      // ==========================================================================
      // 🛡️ PROTEÇÃO RACE CONDITION (3 LOCKS ANTES DE DISPARAR QUALQUER COISA)
      // ==========================================================================

      // Lock 0 (NOVO): tem OUTRAS requests em andamento (persistir_usuario, persistir_escola,
      // salvar config, etc)? → PULA essa rodada (em 10s tenta de novo)
      if (isAnyRequestPendingGlobal()) return;

      // Lock 0.5 (NOVO): identity MUDOU nos últimos 3 segundos?
      // (significa: usuário acabou de fazer LOGIN / IMPERSONATE / LOGOUT / SELECIONAR OUTRA ESCOLA)
      // → Esperamos para o write do localStorage + writes de outros requests acabarem.
      if (didIdentityChangeRecentlyGlobal(3000)) return;

      // Lock 1 (já existia): a própria revalidação já está rodando nesta mesma instância?
      if (revalidacaoRodandoRef.current) return;

      // 🛡️ Snapshot FIXO da identity no momento exato do início da rodada.
      // NÃO usamos o useState 'identity' mutável durante o await (poderia mudar via setState externo).
      const identitySnap = identity;
      if (!identitySnap?.token) return;

      try {
        revalidacaoRodandoRef.current = true;
        const res = await revalidarAuth(identitySnap);
        if (cancelado) return;
        if (res.valido && res.retorno) {
          _setIdentity((prevAtual) => {
            const prev = prevAtual ?? identitySnap
            const retornoBackend = res.retorno as Partial<Identity> | null | undefined
            if (!retornoBackend) return prevAtual

            // SPREAD OPERATOR = preserva TUDO que veio do storage/UI (selected_id / selected_role / impersonated_by)
            // e SÓ atualiza os campos que o backend revalidou realmente (token, exp, dados básicos).
            // Nunca apaga a seleção de perfil do RESPONSAVEL nem o impersonate do SUPER.
            const merged: Identity = {
              ...(prev ?? {} as any),
              ...(retornoBackend as any ?? {}),
              selected_id:    prev?.selected_id    ?? (retornoBackend as any)?.selected_id,
              selected_role:  prev?.selected_role  ?? (retornoBackend as any)?.selected_role,
              impersonated_by: prev?.impersonated_by ?? (retornoBackend as any)?.impersonated_by,
            } as Identity

            for (const chave of Object.keys(merged)) {
              if ((merged as any)[chave] === undefined) delete (merged as any)[chave]
            }

            if (prevAtual) {
              try {
                const normalizadoPrev = JSON.stringify(Object.entries(prevAtual).sort())
                const normalizadoMerged = JSON.stringify(Object.entries(merged).sort())
                if (normalizadoPrev === normalizadoMerged) return prevAtual
              } catch {}
            }

            try { localStorage.setItem(USR, JSON.stringify(merged)) } catch {}
            if (typeof merged.token === 'string' && merged.token.trim() !== '') {
              try { localStorage.setItem(TKN, merged.token) } catch {}
            }
            return merged
          })
        } else if (!res.valido && !res.erroRede) {
          toast.error('Sessão expirada ou inválida. Redirecionando para login.');
          safeLocalRemoveAuth();
          sessaoIniciadaViaLoginRef.current = false;
          _setIdentity(null);
          window.location.hash = '#';
          window.location.replace('/');
        }
      } finally {
        revalidacaoRodandoRef.current = false;
      }
    };

    intervalId = window.setInterval(rodar, 10 * 1000);
    return () => {
      cancelado = true;
      if (intervalId) clearInterval(intervalId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervaloDeveRodar, identity?.token]);

  const login = useCallback(async (user: string, pwd: string): Promise<void> => {
    try {
      const base64 = btoa(`${user}:${pwd}`)
      const headers = appendAppVersionHeaders({
        Authorization: `Basic ${base64}`,
        "Content-Type": "application/json"
      })
      const response = await CapacitorHttp.request({
        url: `${URL}/auth`,
        method: "GET",
        headers
      })

      if (response.status !== 200) throw new Error("Login failed")
      const obj = response.data as Identity
      localStorage.removeItem(SUPER_USR_ORIGINAL)
      localStorage.removeItem(SUPER_TKN_ORIGINAL)

      const completo = await complementarDadosProfessorSeNecessario(obj, post as any)
      setIdentity(completo)
      acionarInicioIntervaloAposLogin()
      window.location.replace("/")
    } catch (err) {
      throw err
    }
  }, [setIdentity, acionarInicioIntervaloAposLogin, post])

  const logout = useCallback(() => {
    markIdentityChangedGlobal()
    desligarIntervaloAposLogout()
    safeLocalRemoveAuth()
    setIdentity(null)
    window.location.hash = "#"
    window.location.replace("/")
  }, [desligarIntervaloAposLogout, setIdentity])

  const startImpersonate = useCallback(async (email: string): Promise<Identity> => {
    const response = await post("/impersonate", { email })
    if (!response.success || !response.data) {
      throw new Error("Não foi possível iniciar o impersonate")
    }
    markIdentityChangedGlobal()
    const superUser = localStorage.getItem(USR)
    const superToken = localStorage.getItem(TKN)
    if (superUser) localStorage.setItem(SUPER_USR_ORIGINAL, superUser)
    if (superToken) localStorage.setItem(SUPER_TKN_ORIGINAL, superToken)
    const obj = response.data as unknown as Identity
    const completo = await complementarDadosProfessorSeNecessario(obj, post as any)
    setIdentity(completo)
    acionarInicioIntervaloAposLogin()
    return completo
  }, [post, setIdentity, acionarInicioIntervaloAposLogin])

  const stopImpersonate = useCallback(() => {
    markIdentityChangedGlobal()
    const originalUser = localStorage.getItem(SUPER_USR_ORIGINAL)
    const originalToken = localStorage.getItem(SUPER_TKN_ORIGINAL)
    localStorage.removeItem(SUPER_USR_ORIGINAL)
    localStorage.removeItem(SUPER_TKN_ORIGINAL)
    if (originalToken) localStorage.setItem(TKN, originalToken)
    else localStorage.removeItem(TKN)
    if (originalUser) {
      const parsed = safeJsonParse<Identity>(originalUser)
      localStorage.setItem(USR, originalUser)
      _setIdentity(parsed)
      acionarInicioIntervaloAposLogin()
    } else {
      localStorage.removeItem(USR)
      desligarIntervaloAposLogout()
      _setIdentity(null)
      window.location.hash = "#"
      window.location.replace("/")
    }
  }, [acionarInicioIntervaloAposLogin, desligarIntervaloAposLogout])

  const value = useMemo<AuthProviderState>(() => ({
    identity,
    isImpersonating,
    setIdentity,
    setSuperSchoolId,
    login,
    logout,
    startImpersonate,
    stopImpersonate
  }), [identity, isImpersonating, setIdentity, login, logout, startImpersonate, stopImpersonate])

  return (
    <AuthProviderContext.Provider value={value}>
      {children}
    </AuthProviderContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthProviderContext)

  if (context === undefined)
    throw new Error("useAuth must be used within an AuthProvider")

  return context
}
